//#region Imports and Global Definitions
import React, { useState, useEffect, useRef } from 'react';
import { StyleSheet, View, SafeAreaView, Platform, useWindowDimensions, FlatList } from 'react-native';
import { useTheme } from '@react-navigation/native';
import { createCommonStyles } from '../../../../styles/commonStyles';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../../components/CustomInput';
import CustomButton from '../../../../components/CustomButton';
import { ActivityIndicator, Card } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import { useCompany } from '../../../../hooks/useCompany';
import { useSettingsContext } from '../../../../hooks/useSettingsContext';
var _ = require('lodash');
import { useTranslation } from 'react-i18next';
//#endregion

const NumberInput = ({ index, item, itemKey, handleEditPress }) => {
    const [number, setNumber] = useState(null);
    return (
        <CustomInput
            defaultValue={item[itemKey]}
            value={number}
            setValue={e => {
                let onlyNum = e.replace(/[^0-9]/g, '');
                setNumber(onlyNum);
                handleEditPress(index, item, itemKey, onlyNum)
            }}
            maxLen={50}
            type="LOCATIONS"
            keyboardType='numeric'
        />
    );
};

const HRAdminLocationsScreen = ({ props }) => {

    //#region Const Definitions
    const { width, height } = useWindowDimensions();
    const isPlatformWeb = (Platform.OS === 'web' && width > 992 ? false : true);
    const { colors } = useTheme();
    const styles = makeStyles(colors, width, height, isPlatformWeb);
    const common = createCommonStyles(colors);
    const { d365 } = useSettingsContext();
    const [isLoading, setIsLoading] = useState(true);
    const [locationsData, setLocationsData] = useState([]);
    const [changes, setChanges] = useState({});
    const [noChangesLocations, setnoChangesLocations] = useState([]);
    const [hasNotProvidedCode, setHasNotProvidedCode] = useState();
    const [refreshAfterSave, setRefreshAfterSave] = useState(false);
    const [locationsCnt, setLocationsCnt] = useState(0);
    const [isDuplicate, setIsDuplicate] = useState();
    const { getLocationsData, insertLocation, deleteLocation, updateLocation } = useCompany();
    const ref = useRef(null);
    const companyRef = useRef(null);
    //#endregion

    // * Language
    const { t, i18n } = useTranslation();

    //#region UseEffect
    useEffect(() => {
        if ((companyRef.current !== null) && companyRef.current !== props.selectedCompany) {
            props.handleGoBack();
            companyRef.current = props.selectedCompany;
            return;
        }
        companyRef.current = props.selectedCompany;
        getLocationsData(props.selectedCompany, props.selectedBranch)
            .then((arr) => {
                setLocationsData(arr);
                setnoChangesLocations([...arr])
                setChanges({});
                setLocationsCnt(arr.length)

                // state variables that track button colors (not filled mandatory field, duplicate code names)
                setHasNotProvidedCode(Array(arr.length).fill(false))
                setIsDuplicate(Array(arr.length).fill(false))
                setRefreshAfterSave(false);
                setIsLoading(false);
            }).catch(() => {
                setRefreshAfterSave(false);
                setIsLoading(false);
            })
    }, [props.selectedCompany, props.selectedBranch, refreshAfterSave])

    //#endregion

    //#region Listeners
    const handleEditPress = (index, item, key, value) => {
        if (item.index in changes) {
            changes[item.index][key] = value;
        }
        else {
            changes[item.index] = { ...item };
            changes[item.index][key] = value;
        }
        if (changes[item.index].isNewLocation === true) changes[item.index].oldLocationID = ""
        locationsData[index] = { ...locationsData[index], [key]: value }
        props.checkIfUnsavedChanges(true);
    }

    const checkForDuplicates = (insertData, updateData) => {
        let duplicateCodes = [];

        const setNoChangesLocations = new Set(noChangesLocations.map((obj) => obj.location.toUpperCase()));

        insertData.forEach((insertLocation) => {
            if (setNoChangesLocations.has(insertLocation.location.toUpperCase())) {
                insertLocation['status'] = 'INSERT';
                insertLocation['index'] = insertLocation.index;
                duplicateCodes.push(insertLocation);
            }
            else if (isDuplicate[insertLocation.index])
                isDuplicate[insertLocation.index] = false;
        })

        updateData.forEach(updateLocation => {
            if (setNoChangesLocations.has(updateLocation.location.toUpperCase()) && updateLocation.oldLocationID.toUpperCase() !== updateLocation.location.toUpperCase()) {
                updateLocation['status'] = 'UPDATE';
                updateLocation['index'] = updateLocation.index;
                duplicateCodes.push(updateLocation);
            }
            else if (isDuplicate[updateLocation.index])
                isDuplicate[updateLocation.index] = false;
        })
        return duplicateCodes;
    };

    const handleDuplicates = (duplicates) => {
        showMessage({
            message: t('common:duplicateBranchesMessage'),
            type: "error",
            duration: 3000,
        })
        let prevStateDuplicate = isDuplicate;
        duplicates.forEach(duplicate => {
            prevStateDuplicate[duplicate.index] = true;
        })
        setIsDuplicate([...prevStateDuplicate]);
    }

    //#region SaveDeleteCancel
    const handleSave = async () => {
        let newPromises = [];
        let insertLocations = []
        let updateExistingLocations = []
        let deleteLocations = []

        let insertLocationsDupl = []; let updateLocationsDupl = [];

        let deleteSucc = true;
        let insertSucc = true;
        let updateSucc = true;

        let updatedArray = [...hasNotProvidedCode];
        let emptyCodeFound = false;


        Object.entries(changes).forEach(([index, change]) => {
            if (change.location == "") {
                updatedArray[change.index] = true;
                emptyCodeFound = true;
            }
            else {
                updatedArray[index] = false;
            }
        });

        setHasNotProvidedCode([...updatedArray]);

        Object.values(changes).forEach((change) => {
            const location = {
                location: change.location,
                branch: props.selectedBranch,
                description: change.description,
                tel: change.tel,
                fax: change.fax,
                addr: change.addr,
                city: change.city,
                postCode: change.postCode,
                province: change.province,
                country: change.country,
                company: props.selectedCompany,
                d365Code: change.d365Code,
                locationDME: change.locationDME,
                oldLocationID: change.oldLocationID
            }
            if (change.isDeleted === true)
                deleteLocations.push(location);
            else if (change.isNewLocation === true) {
                insertLocationsDupl.push(change);
                insertLocations.push(location);
            }
            else {
                updateLocationsDupl.push(change);
                updateExistingLocations.push(location);
            }
        });
        let duplicates = checkForDuplicates(insertLocationsDupl, updateLocationsDupl);
        if (duplicates.length > 0) {
            handleDuplicates(duplicates);
            return; // there are duplicate codes therefore do not save and return
        }

        if (emptyCodeFound) {
            showMessage({
                message: t('common:mandatoryCodeNotGivenLocations'),
                type: "danger",
                duration: 3000,
            })
            return;
        }

        if (updateExistingLocations.length > 0) {
            updateLocation(updateExistingLocations).then((res) => {
                if (!res) updateSucc = false;
                removeChangesAfterSaving(updateExistingLocations);
            }).catch(() => { });
            newPromises.push(updateLocation(updateExistingLocations));
        }
        if (insertLocations.length > 0) {
            insertLocation(insertLocations).then((res) => {
                if (!res) insertSucc = false;
                removeChangesAfterSaving(insertLocations);
            }).catch(() => { })
            newPromises.push(insertLocation(insertLocations));
        }
        if (deleteLocations.length > 0) {
            deleteLocation(deleteLocations).then((res) => {
                if (!res) deleteSucc = false;
                removeChangesAfterSaving(deleteLocations);
            }).catch(() => { })
            newPromises.push(deleteLocation(deleteLocations));
        }
        try {
            await Promise.all(newPromises).then(() => { // when all the callee async functions return, show message based on result
                if (!insertSucc || !deleteSucc || !updateSucc)
                    showMessage({
                        message: t('common:issueMessage'),
                        type: "error",
                        duration: 3000,
                    })
                else if (insertSucc && deleteSucc && updateSucc) {
                    showMessage({
                        message: t('common:allOperationsSuccMess'),
                        type: "success",
                        duration: 3000,
                    })
                    setIsLoading(true);
                    setRefreshAfterSave(true); // fetch updates from db
                    props.checkIfUnsavedChanges(false);
                }
            });
        } catch (error) {
            console.log(error)
        }
    }

    const handleDeleteLocation = (location, index) => {
        if (!(location.index in changes)) {
            setChanges((prevState) => ({
                ...prevState,
                [location.index]: {
                    company: props.selectedCompany, branch: props.selectedBranch, location: location.location, description: location.description,
                    tel: location.tel, fax: location.fax, addr: location.addr, city: location.city, province: location.province,
                    country: location.country, postCode: location.postCode, d365Code: location.d365Code, locationDME: location.locationDME, isDeleted: true
                }
            }));
        }
        else {
            changes[location.index]["isDeleted"] = true
        }
        if (isDuplicate[location.index]) {
            const updatedLocations = [...isDuplicate];
            updatedLocations[location.index] = false;
            setIsDuplicate(updatedLocations);
        }
        if (hasNotProvidedCode[location.index]) {
            const updatedLocations = [...hasNotProvidedCode];
            updatedLocations[location.index] = false;
            setHasNotProvidedCode(updatedLocations);
        }
        let array = [...locationsData];
        array.splice(index, 1);
        setLocationsData(array);
        setLocationsCnt(locationsCnt - 1)
    }

    const handleAddNewLocation = () => {
        let array = [];
        const hasEmptyLine = containsEmptyLine();
        if (hasEmptyLine) {
            showMessage({
                message: t('common:multEmptyLines'),
                type: "danger",
                duration: 10000,
            })
        }
        else {
            let item = {
                location: "",
                company: props.selectedCompany,
                branch: props.selectedBranch,
                description: "",
                tel: "",
                fax: "",
                addr: "",
                city: "",
                province: "",
                country: "",
                postCode: "",
                d365Code: "",
                locationDME: "",
                isNewLocation: true,
                oldLocationID: "",
                index: locationsCnt
            }
            array = [item, ...locationsData];
            changes[item.index] = { ...item };
            changes[locationsCnt]['location'] = "";
            setLocationsCnt(locationsCnt + 1);
            scrollToTop();
            setIsDuplicate(prevState => [...prevState, false]);
            setLocationsData(array);
        }
    }

    const handleCancelChanges = () => {
        setLocationsData([...noChangesLocations]);
        setIsDuplicate(Array(noChangesLocations.length).fill(false));
        setHasNotProvidedCode(Array(noChangesLocations.length).fill(false));
        setChanges({});
        props.checkIfUnsavedChanges(false);
    }
    //#endregion

    const scrollToTop = () => {
        ref.current?.scrollToOffset({ y: 0, animated: true })
    }

    const removeChangesAfterSaving = (locationChanges) => {
        Object.entries(changes).forEach(([index, location]) => {
            locationChanges.forEach(locationChange => {
                if (location.index == locationChange.index) {
                    delete changes[location.index];
                }
            })
        });
    }

    const containsEmptyLine = () => {
        return locationsData.some(location =>
            location.location === "" &&
            location.description === "" &&
            location.tel === "" &&
            location.fax === "" &&
            location.addr === "" &&
            location.city === "" &&
            location.province === "" &&
            location.country === "" &&
            location.postCode === "" &&
            location.d365Code === "" &&
            location.locationDME === ""
        )
    }
    //#endregion

    //#region Components
    const LocationListView = ({ item, index }) => {
        const [showAdditionalLocationInfo, setAdditionalLocationInfo] = useState(false);

        useEffect(() => {
            //TODO
        }, [showAdditionalLocationInfo])

        const onShowAdditionalLocationInfoPress = (item) => {
            setAdditionalLocationInfo(!showAdditionalLocationInfo)
        }

        return (
            <View>
                <SafeAreaView>
                    <SafeAreaView>
                        <SafeAreaView
                            style={styles.row}>

                            {/* //* Always Visible */}
                            <SafeAreaView
                                style={styles.itemrow}>
                                <CustomLabel
                                    value={t('common:location')}
                                    type='SUBTITLENOFLEX' />

                                <CustomInput
                                    defaultValue={item.location}
                                    setValue={(e) => handleEditPress(index, item, 'location', e)}
                                    maxLen={5}
                                    type={hasNotProvidedCode[item.index] || isDuplicate[item.index] ? "MISSING_LOCATIONS_CODE" : "MAININPUT"}
                                />
                            </SafeAreaView>
                            <SafeAreaView
                                style={styles.itemrow}>
                                <CustomLabel
                                    value={t('common:description')}
                                    type='SUBTITLENOFLEX' />
                                <CustomInput
                                    defaultValue={item.description}
                                    setValue={(e) => handleEditPress(index, item, 'description', e)}
                                    maxLen={50}
                                    type="MAININPUT"
                                />
                            </SafeAreaView>
                            <SafeAreaView>
                                <CustomButton
                                    iconName={!showAdditionalLocationInfo ? 'chevron-forward-outline' : 'chevron-down-outline'}
                                    onPress={() => {
                                        onShowAdditionalLocationInfoPress(item, index)
                                    }}
                                    text={t('common:additionalInfos')}
                                    show='PART'
                                    type='PART'
                                    textTop='0'
                                    sz={30} />
                            </SafeAreaView>
                            <SafeAreaView style={styles.justMargin}>
                                <CustomButton
                                    iconName='trash-outline'
                                    onPress={() => {
                                        handleDeleteLocation(item, index)
                                    }}
                                    show='PART'
                                    type='PART'
                                    textTop='2'
                                    sz={30} />
                            </SafeAreaView>
                        </SafeAreaView>

                        {showAdditionalLocationInfo &&
                            <SafeAreaView style={styles.row}>

                                <SafeAreaView
                                    style={styles.item}>
                                    <CustomLabel
                                        value={t('common:officeAddress')}
                                        type='SUBTITLE' />


                                    <CustomInput
                                        defaultValue={item.addr}
                                        setValue={(e) => handleEditPress(index, item, 'addr', e)}
                                        maxLen={200}
                                        multiline={true}
                                        numberOfLines={5}
                                        type="MAININPUT"
                                    />
                                </SafeAreaView>
                                <SafeAreaView
                                    style={styles.item}>
                                    <CustomLabel
                                        value={t('common:city')}
                                        type='SUBTITLE' />


                                    <CustomInput
                                        defaultValue={item.city}
                                        setValue={(e) => handleEditPress(index, item, 'city', e)}
                                        maxLen={50}
                                        type="MAININPUT"
                                    />
                                </SafeAreaView>
                                <SafeAreaView
                                    style={styles.item}>
                                    <CustomLabel
                                        value={t('common:postCode')}
                                        type='SUBTITLE' />


                                    <CustomInput
                                        defaultValue={item.postCode}
                                        setValue={(e) => handleEditPress(index, item, 'postCode', e)}
                                        maxLen={50}
                                        type="MAININPUT"
                                    />
                                </SafeAreaView>
                                <SafeAreaView
                                    style={styles.item}>
                                    <CustomLabel
                                        value={t('common:province')}
                                        type='SUBTITLE' />


                                    <CustomInput
                                        defaultValue={item.province}
                                        setValue={(e) => handleEditPress(index, item, 'province', e)}
                                        maxLen={50}
                                        type="MAININPUT"
                                    />
                                </SafeAreaView>
                                <SafeAreaView
                                    style={styles.item}>
                                    <CustomLabel
                                        value={t('common:country')}
                                        type='SUBTITLE' />


                                    <CustomInput
                                        defaultValue={item.country}
                                        setValue={(e) => handleEditPress(index, item, 'country', e)}
                                        maxLen={50}
                                        type="MAININPUT"
                                    />
                                </SafeAreaView>
                                <SafeAreaView
                                    style={styles.item}>
                                    <CustomLabel
                                        value={t('common:regionalCode')}
                                        type='SUBTITLE' />

                                    <CustomInput
                                        defaultValue={item.locationDME}
                                        setValue={(e) => handleEditPress(index, item, 'locationDME', e)}
                                        maxLen={50}
                                        type="MAININPUT"
                                    />
                                </SafeAreaView>
                                <SafeAreaView
                                    style={styles.item}>
                                    <CustomLabel
                                        value={t('common:telNumber')}
                                        type='SUBTITLE' />


                                    <NumberInput
                                        index={index}
                                        item={item}
                                        itemKey="tel"
                                        handleEditPress={handleEditPress}
                                    />
                                </SafeAreaView>
                                <SafeAreaView
                                    style={styles.item}>
                                    <CustomLabel
                                        value={t('common:faxNumber')}
                                        type='SUBTITLE' />


                                    <NumberInput
                                        index={index}
                                        item={item}
                                        itemKey="fax"
                                        handleEditPress={handleEditPress}
                                    />
                                </SafeAreaView>
                                {d365 &&
                                    <SafeAreaView
                                        style={styles.item}>
                                        <CustomLabel
                                            value={t('common:d365Code')}
                                            type='SUBTITLE' />

                                        <CustomInput
                                            defaultValue={item.d365Code}
                                            setValue={(e) => handleEditPress(index, item, 'd365Code', e)}
                                            maxLen={50}
                                            type="MAININPUT"
                                        />
                                    </SafeAreaView>
                                }
                            </SafeAreaView>
                        }
                    </SafeAreaView>
                </SafeAreaView>
            </View >
        )
    }

    const renderLocations = (item, index) => {
        return (
            <Card
                style={[common.cardstyle]} >
                <Card.Content>
                    <LocationListView item={item} index={index} />
                </Card.Content>
            </Card >
        )
    };

    //#endregion

    //#region Loading
    if (isLoading)
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating={true} color={colors.blue} />
            </View>
        )
    //#endregion

    return (
        <SafeAreaView style={styles.mainView}>
            <SafeAreaView style={styles.rowView}>
                <SafeAreaView style={[styles.subMain]}>
                    {props.selectedCompany &&
                        <View style={styles.innerView}>
                            <View style={[styles.buttons, styles.functionalityButtons]}>
                                {props.selectedBranch !== "" &&
                                    (
                                        <>
                                            <CustomButton
                                                text={t('common:new')}
                                                onPress={() => handleAddNewLocation()}
                                                type='BUTTON_ADD'
                                                textTop='0'
                                                sz={15}
                                            />
                                            {((Object.keys(changes).length > 0 && Object.values(changes).some(innerObj => !innerObj.hasOwnProperty('isNewLocation'))) || locationsData.length > 0) &&
                                                <CustomButton
                                                    text={t('common:saveChanges')}
                                                    onPress={() => handleSave()}
                                                    type='BUTTON_SAVE'
                                                    textTop='0'
                                                    isGradient={true}
                                                    sz={15}
                                                />
                                            }
                                            <CustomButton
                                                text={t('common:cancel')}
                                                onPress={() => handleCancelChanges()}
                                                type="BUTTON_CANCEL"
                                                sz={15}
                                            />
                                        </>
                                    )}
                            </View>
                            <SafeAreaView style={common.divider} />
                            <FlatList
                                style={styles.locationsFlatlist}
                                data={locationsData}
                                keyExtractor={(item, index) => `${item.location}-${index}`}
                                renderItem={({ item, index }) =>
                                    renderLocations(item, index)
                                }
                                scrollEnabled={true}
                                refreshing={isLoading}
                                ref={ref}
                                initialNumToRender={4}
                                ListEmptyComponent={
                                    isLoading ?
                                        <ActivityIndicator animating={true} color={colors.blue} />
                                        :
                                        <CustomLabel
                                            value={t("common:locationsLoadingPreview")}
                                            type="NO_BRANCHES"
                                        />
                                }
                            />

                        </View>
                    }
                </SafeAreaView>
            </SafeAreaView >
        </SafeAreaView >
    )
};

//#region Styles
const makeStyles = (colors, width) => StyleSheet.create({
    mainView: {
        flex: 1,
    },
    subMain: {
        borderRadius: 35,
        elevation: (Platform.OS === 'web' && width > 992 ? 3 : 0),
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        width: '100%'
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center'
    },
    justMargin: {
        marginHorizontal: 10,
    },
    row: {
        flex: 1,
        margin: 10,
        flexDirection: "row",
        alignItems: 'center',
    },
    item: {
        flex: 1,
        marginVertical: 5,
    },
    itemrow: {
        flex: 1,
        flexDirection: 'row',
    },
    functionalityButtons: {
        justifyContent: 'flex-end',
        marginVertical: 5,
    },
    locationsFlatlist: {
        height: '52%',
        marginVertical: 10,
    },
    innerView: {
        flex: 1,
    },
    buttons: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        width: '50%',
        alignSelf: 'flex-end',
    },
    loader: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4%'
    },
});


//#endregion
export default HRAdminLocationsScreen;